import * as React from "react"
import { motion, AnimatePresence } from "motion/react"
import { Save, RotateCcw, Building2, User, Globe, Clock, X, Search, Plus, Check, Loader2 } from "lucide-react"
import type { IOutcomeType, IOutcomeRanges } from "@/lib/slotMachineTypes"
import { useAuth } from "@/contexts/AuthContext"

interface SaveOptionsPanelProps {
  outcomeTypes: IOutcomeType[]
  outcomeRanges: IOutcomeRanges
  onSaveSuccess?: () => void
  onRevert?: () => void
  disabled?: boolean
}

interface Agency {
  agencyId: string
  agencyName: string
}

interface UserResult {
  id: string
  email: string
  displayName: string | null
  agencyName: string | null
}

type Scope = "system" | "agency" | "user"

const CASINO_COLORS = {
  bg: "#121220",
  bgLight: "#1a1a2e",
  orange: "#F37020",
  gold: "#FFD700",
  green: "#32CD32",
  red: "#FF4444",
  purple: "#9B59B6",
  border: "rgba(255,255,255,0.1)",
  text: "rgba(255,255,255,0.9)",
  textMuted: "rgba(255,255,255,0.6)",
}

export function SaveOptionsPanel({
  outcomeTypes,
  outcomeRanges,
  onSaveSuccess,
  onRevert,
  disabled = false,
}: SaveOptionsPanelProps) {
  const { token } = useAuth()
  
  // Scope selection
  const [scope, setScope] = React.useState<Scope>("system")
  
  // Agency selection (for scope=agency)
  const [agencies, setAgencies] = React.useState<Agency[]>([])
  const [selectedAgencyId, setSelectedAgencyId] = React.useState<string>("")
  const [loadingAgencies, setLoadingAgencies] = React.useState(false)
  
  // User selection (for scope=user)
  const [userQuery, setUserQuery] = React.useState("")
  const [userResults, setUserResults] = React.useState<UserResult[]>([])
  const [selectedUsers, setSelectedUsers] = React.useState<UserResult[]>([])
  const [searchingUsers, setSearchingUsers] = React.useState(false)
  
  // Time limit
  const [hasExpiry, setHasExpiry] = React.useState(false)
  const [expiryDate, setExpiryDate] = React.useState("")
  const [expiryTime, setExpiryTime] = React.useState("23:59")
  
  // Save state
  const [saving, setSaving] = React.useState(false)
  const [reverting, setReverting] = React.useState(false)
  const [toast, setToast] = React.useState<{ type: "success" | "error"; message: string } | null>(null)
  
  // Fetch agencies when scope changes to agency
  React.useEffect(() => {
    if (scope === "agency" && agencies.length === 0) {
      fetchAgencies()
    }
  }, [scope])
  
  // Search users with debounce
  React.useEffect(() => {
    if (scope !== "user" || userQuery.length < 2) {
      setUserResults([])
      return
    }
    const timeout = setTimeout(() => searchUsers(userQuery), 300)
    return () => clearTimeout(timeout)
  }, [userQuery, scope])
  
  // Auto-hide toast
  React.useEffect(() => {
    if (toast) {
      const timeout = setTimeout(() => setToast(null), 4000)
      return () => clearTimeout(timeout)
    }
  }, [toast])
  
  const fetchAgencies = async () => {
    setLoadingAgencies(true)
    try {
      const res = await fetch("/api/admin/agencies", {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (data.success && data.agencies) {
        setAgencies(data.agencies)
        if (data.agencies.length > 0) {
          setSelectedAgencyId(data.agencies[0].agencyId)
        }
      }
    } catch (err) {
      console.error("Failed to fetch agencies:", err)
    } finally {
      setLoadingAgencies(false)
    }
  }
  
  const searchUsers = async (query: string) => {
    setSearchingUsers(true)
    try {
      const res = await fetch(`/api/admin/users/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (data.success && data.users) {
        // Filter out already selected users
        const selectedIds = new Set(selectedUsers.map(u => u.id))
        setUserResults(data.users.filter((u: UserResult) => !selectedIds.has(u.id)))
      }
    } catch (err) {
      console.error("Failed to search users:", err)
    } finally {
      setSearchingUsers(false)
    }
  }
  
  const addUser = (user: UserResult) => {
    setSelectedUsers(prev => [...prev, user])
    setUserResults(prev => prev.filter(u => u.id !== user.id))
    setUserQuery("")
  }
  
  const removeUser = (userId: string) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId))
  }
  
  const handleSave = async () => {
    // Validate
    if (scope === "agency" && !selectedAgencyId) {
      setToast({ type: "error", message: "Please select an agency" })
      return
    }
    if (scope === "user" && selectedUsers.length === 0) {
      setToast({ type: "error", message: "Please select at least one user" })
      return
    }
    
    setSaving(true)
    try {
      // Build expiry date
      let expiresAt: string | undefined
      if (hasExpiry && expiryDate) {
        expiresAt = new Date(`${expiryDate}T${expiryTime}`).toISOString()
      }
      
      // Build request body based on scope
      const body: any = {
        scope,
        outcomeTypes,
        outcomeRanges,
        expiresAt,
      }
      
      if (scope === "agency") {
        body.targetId = selectedAgencyId
      } else if (scope === "user") {
        // For user scope, save one setting per user
        body.targetIds = selectedUsers.map(u => u.id)
      }
      
      const res = await fetch("/api/admin/slot-settings", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      })
      
      const data = await res.json()
      if (data.success) {
        setToast({ type: "success", message: "Settings saved successfully!" })
        onSaveSuccess?.()
      } else {
        setToast({ type: "error", message: data.message || "Failed to save settings" })
      }
    } catch (err) {
      console.error("Save error:", err)
      setToast({ type: "error", message: "Failed to save settings" })
    } finally {
      setSaving(false)
    }
  }
  
  const handleRevert = async () => {
    setReverting(true)
    try {
      onRevert?.()
      setToast({ type: "success", message: "Reverted to default settings" })
    } finally {
      setReverting(false)
    }
  }

  return (
    <div
      className="rounded-xl p-4 mt-4 border"
      style={{
        backgroundColor: CASINO_COLORS.bgLight,
        borderColor: CASINO_COLORS.border,
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Save className="w-5 h-5" style={{ color: CASINO_COLORS.orange }} />
        <span className="font-outfit font-semibold text-base" style={{ color: CASINO_COLORS.text }}>
          Save Options
        </span>
      </div>
      
      {/* Scope Selection */}
      <div className="mb-4">
        <label className="text-sm font-outfit mb-2 block" style={{ color: CASINO_COLORS.textMuted }}>
          Apply To
        </label>
        <div className="flex flex-wrap gap-2">
          {[
            { value: "system" as Scope, label: "System-wide", icon: Globe },
            { value: "agency" as Scope, label: "Per-Agency", icon: Building2 },
            { value: "user" as Scope, label: "Per-User", icon: User },
          ].map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => setScope(value)}
              disabled={disabled}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border transition-all"
              style={{
                backgroundColor: scope === value ? `${CASINO_COLORS.orange}20` : CASINO_COLORS.bg,
                borderColor: scope === value ? CASINO_COLORS.orange : CASINO_COLORS.border,
                color: scope === value ? CASINO_COLORS.orange : CASINO_COLORS.textMuted,
              }}
            >
              <Icon className="w-4 h-4" />
              <span className="text-sm font-outfit">{label}</span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Agency Selector */}
      {scope === "agency" && (
        <div className="mb-4">
          <label className="text-sm font-outfit mb-2 block" style={{ color: CASINO_COLORS.textMuted }}>
            Select Agency
          </label>
          {loadingAgencies ? (
            <div className="flex items-center gap-2 text-sm" style={{ color: CASINO_COLORS.textMuted }}>
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading agencies...
            </div>
          ) : (
            <select
              value={selectedAgencyId}
              onChange={(e) => setSelectedAgencyId(e.target.value)}
              disabled={disabled}
              className="w-full px-3 py-2 rounded-lg border text-sm font-outfit"
              style={{
                backgroundColor: CASINO_COLORS.bg,
                borderColor: CASINO_COLORS.border,
                color: CASINO_COLORS.text,
              }}
            >
              {agencies.map((agency) => (
                <option key={agency.agencyId} value={agency.agencyId}>
                  {agency.agencyName}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
      
      {/* User Selector */}
      {scope === "user" && (
        <div className="mb-4">
          <label className="text-sm font-outfit mb-2 block" style={{ color: CASINO_COLORS.textMuted }}>
            Search Users
          </label>
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              style={{ color: CASINO_COLORS.textMuted }}
            />
            <input
              type="text"
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              placeholder="Search by name or email..."
              disabled={disabled}
              className="w-full pl-10 pr-3 py-2 rounded-lg border text-sm font-outfit"
              style={{
                backgroundColor: CASINO_COLORS.bg,
                borderColor: CASINO_COLORS.border,
                color: CASINO_COLORS.text,
              }}
            />
          </div>
          
          {/* Search results */}
          {userResults.length > 0 && (
            <div
              className="mt-2 rounded-lg border max-h-40 overflow-y-auto"
              style={{ backgroundColor: CASINO_COLORS.bg, borderColor: CASINO_COLORS.border }}
            >
              {userResults.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => addUser(user)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/5 transition-colors"
                >
                  <div className="text-left">
                    <span className="text-sm font-outfit block" style={{ color: CASINO_COLORS.text }}>
                      {user.displayName || user.email}
                    </span>
                    {user.agencyName && (
                      <span className="text-xs font-outfit" style={{ color: CASINO_COLORS.textMuted }}>
                        {user.agencyName}
                      </span>
                    )}
                  </div>
                  <Plus className="w-4 h-4" style={{ color: CASINO_COLORS.green }} />
                </button>
              ))}
            </div>
          )}
          
          {searchingUsers && (
            <div className="mt-2 flex items-center gap-2 text-sm" style={{ color: CASINO_COLORS.textMuted }}>
              <Loader2 className="w-4 h-4 animate-spin" />
              Searching...
            </div>
          )}
          
          {/* Selected users */}
          {selectedUsers.length > 0 && (
            <div className="mt-3">
              <span className="text-xs font-outfit mb-1 block" style={{ color: CASINO_COLORS.textMuted }}>
                Selected ({selectedUsers.length})
              </span>
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map((user) => (
                  <span
                    key={user.id}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-outfit"
                    style={{ backgroundColor: `${CASINO_COLORS.purple}30`, color: CASINO_COLORS.purple }}
                  >
                    {user.displayName || user.email}
                    <button type="button" onClick={() => removeUser(user.id)} className="hover:opacity-70">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Time Limit */}
      <div className="mb-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={hasExpiry}
            onChange={(e) => setHasExpiry(e.target.checked)}
            disabled={disabled}
            className="w-4 h-4 rounded accent-orange-500"
          />
          <Clock className="w-4 h-4" style={{ color: CASINO_COLORS.textMuted }} />
          <span className="text-sm font-outfit" style={{ color: CASINO_COLORS.textMuted }}>
            Set expiry time
          </span>
        </label>
        
        {hasExpiry && (
          <div className="mt-2 flex flex-col sm:flex-row gap-2">
            <input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              disabled={disabled}
              className="flex-1 px-3 py-2 rounded-lg border text-sm font-outfit"
              style={{
                backgroundColor: CASINO_COLORS.bg,
                borderColor: CASINO_COLORS.border,
                color: CASINO_COLORS.text,
              }}
            />
            <input
              type="time"
              value={expiryTime}
              onChange={(e) => setExpiryTime(e.target.value)}
              disabled={disabled}
              className="w-full sm:w-32 px-3 py-2 rounded-lg border text-sm font-outfit"
              style={{
                backgroundColor: CASINO_COLORS.bg,
                borderColor: CASINO_COLORS.border,
                color: CASINO_COLORS.text,
              }}
            />
          </div>
        )}
      </div>
      
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={disabled || saving}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-outfit font-medium text-sm transition-all disabled:opacity-50"
          style={{ backgroundColor: CASINO_COLORS.orange, color: "#000" }}
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? "Saving..." : "Save Settings"}
        </button>
        
        <button
          type="button"
          onClick={handleRevert}
          disabled={disabled || reverting}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-outfit text-sm transition-all disabled:opacity-50 border"
          style={{
            backgroundColor: "transparent",
            borderColor: CASINO_COLORS.border,
            color: CASINO_COLORS.textMuted,
          }}
        >
          {reverting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RotateCcw className="w-4 h-4" />
          )}
          Revert to Default
        </button>
      </div>
      
      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed bottom-4 right-4 px-4 py-3 rounded-lg flex items-center gap-2 shadow-lg z-50"
            style={{
              backgroundColor: toast.type === "success" ? CASINO_COLORS.green : CASINO_COLORS.red,
              color: "#fff",
            }}
          >
            {toast.type === "success" ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
            <span className="text-sm font-outfit">{toast.message}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
